import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@siliconx/shared/ui/Button';
import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';
import type { BenchRunDetail } from '@siliconx/shared';
import { acknowledgeBenchReuse } from '../../lib/bench';
import { useAuth } from '../../lib/authContext';
import { qk } from '../../lib/queryKeys';
import { formatBenchWhen } from './benchTime';

/**
 * SIL-1708 (security S7). A `resume_reuse` means a resume token was presented AGAIN after it was used: a possible cloned NVRAM or
 * USB token. An org admin may acknowledge it, which RELEASES THE COMMIT HOLD (it does not restore a replaced session), so the
 * acknowledgement must be informed: the count and the time of every open event are listed first. The button quotes the newest
 * event id the admin was shown; if a newer one arrived the server answers stale_view and the page refetches and asks again.
 */

export const REUSE_RELEASE_NOTE =
  'Acknowledging releases the hold on committing this run. It does not restore a session that was replaced.';

function ackErrorMessage(err: unknown): string {
  const code = (err as { body?: { error?: { code?: string } } } | null)?.body?.error?.code;
  switch (code) {
    case 'stale_view': return 'New reuse events arrived while you were reading. Review them below, then acknowledge again.';
    case 'none_open': return 'There is nothing left to acknowledge.';
    case 'run_not_running': return 'That run is no longer running.';
    case 'try_again': return 'The system is busy. Try again in a moment.';
    case 'not_found': return 'That run was not found.';
    default: return 'The acknowledgement failed. Try again.';
  }
}

export function ReuseAcknowledgeBanner({ run }: { run: BenchRunDetail }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [message, setMessage] = useState<string | null>(null);
  const reuse = run.reuse;

  const ack = useMutation({
    mutationFn: (eventId: string) => acknowledgeBenchReuse(run.id, eventId),
    onMutate: () => setMessage(null),
    onError: (err) => setMessage(ackErrorMessage(err)),
    // Success, stale_view and a lost 200 all end the same way: refetch, and the run says what is still open.
    onSettled: () => void queryClient.invalidateQueries({ queryKey: qk.bench.run(run.id) }),
  });

  if (!reuse?.open) return null;
  const hidden = reuse.count - reuse.events.length;

  return (
    <InsightBanner tone="amber" lead="A resume token was presented again">
      <div className="space-y-2">
        <p>
          This run&apos;s resume token was presented {reuse.count === 1 ? 'again once' : `again ${reuse.count} times`} after it had already
          been used. That can mean the token was copied. The run cannot be committed until an org admin acknowledges it.
        </p>
        <div>
          <p className="text-xs font-medium">
            {reuse.count === 1 ? '1 open event' : `${reuse.count} open events`}
          </p>
          <ul className="list-disc pl-5" data-testid="reuse-events">
            {reuse.events.map((e) => (
              <li key={e.id}>{formatBenchWhen(e.createdAt)}</li>
            ))}
          </ul>
          {hidden > 0 && <p className="text-xs">Showing the newest {reuse.events.length} of {reuse.count}.</p>}
        </div>
        <p>{REUSE_RELEASE_NOTE}</p>
        {message && (
          <p role="alert" className="text-status-red">
            {message}
          </p>
        )}
        {isAdmin ? (
          <Button
            variant="secondary"
            onClick={() => reuse.latestReuseEventId && ack.mutate(reuse.latestReuseEventId)}
            disabled={ack.isPending || !reuse.latestReuseEventId}
          >
            Acknowledge and release the hold
          </Button>
        ) : (
          <p className="font-medium">An org admin must acknowledge this.</p>
        )}
      </div>
    </InsightBanner>
  );
}
